/**
 * Choix de la langue.
 *
 * A deux langues, une rangee de deux boutons suffisait. A neuf, la meme rangee
 * occuperait deux lignes de pied de page, ferait defiler le reste hors de vue
 * et obligerait a lire neuf mots pour en trouver un. On montre donc la langue
 * COURANTE, et la liste ne s'ouvre qu'a la demande.
 *
 * C'est un `<select>` natif, habille aux tokens : il apporte gratuitement la
 * navigation clavier, la recherche par frappe, la fermeture par Echap et,
 * surtout, la roue native sur telephone, qui reste la meilleure liste longue
 * qu'on puisse offrir a un pouce. Un menu maison aurait demande une gestion du
 * focus, un clic exterieur et un piege a focus, pour un resultat moins bon.
 *
 * Le vrai `<select>` est transparent PAR-DESSUS l'habillage : c'est lui qui
 * recoit le clic et le focus, l'habillage ne fait qu'afficher. Sans cela il
 * faudrait synchroniser deux elements.
 */
import type { ReactElement } from 'react';
import { LANGUAGES, LANGUAGE_NAMES, isLanguage } from '../locales/index.js';
import { useT } from '../locales/useT.js';
import { useUiStore } from '../store/uiStore.js';
import { GlobeIcon, ChevronDownIcon } from './icons.js';

export function LanguagePicker(): ReactElement {
  const t = useT();
  const language = useUiStore((state) => state.language);
  const setLanguage = useUiStore((state) => state.setLanguage);

  return (
    <div className="lang-picker">
      {/* Couche purement visuelle : le lecteur d'ecran annonce deja la langue
          courante en lisant la valeur du select, l'entendre deux fois de suite
          n'apprend rien. */}
      <span className="lang-picker__face" aria-hidden="true">
        <GlobeIcon />
        <span className="lang-picker__current" lang={language}>
          {LANGUAGE_NAMES[language]}
        </span>
        <ChevronDownIcon />
      </span>

      <select
        className="lang-picker__select"
        aria-label={t('common.language')}
        value={language}
        onChange={(event) => {
          const next = event.target.value;
          if (isLanguage(next)) setLanguage(next);
        }}
      >
        {LANGUAGES.map((code) => (
          <option key={code} value={code} lang={code}>
            {LANGUAGE_NAMES[code]}
          </option>
        ))}
      </select>
    </div>
  );
}
